import type { ReactNode, Ref } from 'react'
import type { Switch as SwitchNS } from '@base-ui/react/switch'
import { Switch as BaseSwitch } from '@base-ui/react/switch'

import { cn } from '@gto/ui/cn'

import { switchVariants } from './switch.styles'

type SwitchColor = 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
type SwitchSize = 'small' | 'medium'

type SwitchOwnProps = {
  /** Whether the switch is checked (controlled) */
  checked?: boolean
  /** Initial checked state (uncontrolled) */
  defaultChecked?: boolean
  /** Callback when the checked state changes */
  onCheckedChange?: SwitchNS.Root.Props['onCheckedChange']
  /** Whether the switch is disabled */
  disabled?: boolean
  /** Whether the switch is read-only */
  readOnly?: boolean
  /** Whether the switch is required */
  required?: boolean
  /** Color variant */
  color?: SwitchColor
  /** Tints the checked state from the caller's --accent-rgb, --accent-solid, --accent-bg-a and --accent-border-a, with green fallbacks. */
  accent?: 'custom'
  /** Size variant */
  size?: SwitchSize
  /** Form field name */
  name?: string
  /** Additional CSS classes for the root */
  className?: string
  /** React 19 ref prop */
  ref?: Ref<HTMLButtonElement>
  /** Ref for the hidden input element */
  inputRef?: Ref<HTMLInputElement>
  /** Optional content rendered inside the thumb (e.g. icons) */
  children?: ReactNode
}

type SwitchProps = SwitchOwnProps &
  Omit<React.HTMLAttributes<HTMLElement>, keyof SwitchOwnProps | 'onChange'>

const Switch = ({
  checked,
  defaultChecked,
  onCheckedChange,
  disabled = false,
  readOnly = false,
  required = false,
  color = 'primary',
  accent,
  size = 'medium',
  name,
  className,
  ref,
  inputRef,
  children,
  ...rest
}: SwitchProps) => {
  const styles = switchVariants({ size, color, accent, disabled })

  return (
    <BaseSwitch.Root
      ref={ref}
      checked={checked}
      className={cn(styles.root(), className)}
      defaultChecked={defaultChecked}
      disabled={disabled}
      inputRef={inputRef}
      name={name}
      readOnly={readOnly}
      required={required}
      onCheckedChange={onCheckedChange}
      {...rest}
    >
      <BaseSwitch.Thumb className={styles.thumb()}>{children}</BaseSwitch.Thumb>
    </BaseSwitch.Root>
  )
}

export { Switch }
export type { SwitchColor, SwitchProps, SwitchSize }
